import React, { useCallback, useEffect, useState } from "react";
import { PiEye } from "react-icons/pi";
import axios from "../../axios";
import apiError from "../../error";
import swalAlert from "../../swalFunction";
import { getDate } from "../../function";
import "./ai-training.css";

// Read-only training tab for the campaign draft preview.
// Mirrors the courses listing from the create-template preview, but the courses
// are shown for view purposes only and cannot be added, removed or configured.
const AiTraining = ({ courseIds = [] }) => {
  const [courses, setCourses] = useState([]);
  const [imageURL, setImageURL] = useState("");
  const [previewURL, setPreviewURL] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchCourseDetails = useCallback(async () => {
    if (!courseIds.length) {
      setCourses([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get("/api/lms/course-detail", { params: { courseIds } });
      setCourses(Array.isArray(res.data?.courses) ? res.data.courses : []);
      setImageURL(res.data?.imageURL || "");
      setPreviewURL(res.data?.previewURL || "");
    } catch (err) {
      apiError(err);
      setCourses([]);
    } finally {
      setLoading(false);
    }
    // courseIds is an array; join to keep the dependency stable.
    // eslint-disable-next-line
  }, [courseIds.join(",")]);

  useEffect(() => {
    fetchCourseDetails();
  }, [fetchCourseDetails]);

  const handleViewCourse = async (courseId) => {
    document.body.style.cursor = "wait";
    try {
      const res = await axios.get("/api/lms", {
        params: { url: `${previewURL}${courseId}?r=Sw4s56g` },
      });
      document.body.style.cursor = "";
      const allowedBaseURL = process.env.REACT_APP_LMS_URL;
      const allowedDomain = new URL(allowedBaseURL).hostname;
      const url = new URL(res.data.url);

      const allowedDomainParts = allowedDomain.split(".").slice(-2).join(".");
      const urlDomainParts = url.hostname.split(".").slice(-2).join(".");

      // Validate domain
      if (urlDomainParts !== allowedDomainParts) {
        swalAlert("Security Alert", "Invalid redirect URL detected!", "error");
        return;
      }

      // Prevent Open Redirection
      const redirectUrl = url.searchParams.get("url");
      if (redirectUrl) {
        try {
          const parsedRedirectUrl = new URL(redirectUrl, allowedBaseURL);
          const redirectDomainParts = parsedRedirectUrl.hostname.split(".").slice(-2).join(".");
          if (redirectDomainParts !== allowedDomainParts) {
            swalAlert("Security Alert", "Invalid redirect target detected!", "error");
            return;
          }
        } catch (error) {
          swalAlert("Security Alert", "Invalid redirect target detected!", "error");
          return;
        }
      }
      window.open(res.data.url, "_blank");
    } catch (err) {
      apiError(err);
      document.body.style.cursor = "";
    }
  };

  return (
    <div className="at-wrapper">
      <div className="at-header">
        <h6 className="at-title">Training</h6>
        <p className="at-subtitle">Courses assigned to this campaign</p>
      </div>

      <div className="at-table-wrap">
        <table className="at-table">
          <thead>
            <tr>
              <th>Course</th>
              <th>Date</th>
              <th>Lectures</th>
              <th>Status</th>
              <th className="at-th-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="at-empty">Loading courses…</td>
              </tr>
            ) : courses.length === 0 ? (
              <tr>
                <td colSpan={5} className="at-empty">No training courses linked to this campaign.</td>
              </tr>
            ) : (
              courses.map((course, index) => (
                <tr key={course.id != null ? course.id : index}>
                  <td>
                    <div className="at-course-cell">
                      <div className="at-course-logo">
                        {course.logo ? (
                          <img src={`${imageURL}${course.logo}`} alt={course.name || "course"} />
                        ) : (
                          <span className="at-course-logo-fallback">
                            {(course.name || "C").charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="at-course-info">
                        <p className="at-course-name">{course.name || "--"}</p>
                        {course.courseType ? <p className="at-course-type">{course.courseType}</p> : null}
                      </div>
                    </div>
                  </td>
                  <td>{getDate(course.createdAt)}</td>
                  <td>{course.totalLecture ?? "--"}</td>
                  <td>{course.status || "--"}</td>
                  <td className="at-td-center">
                    <button
                      type="button"
                      className="at-view-btn"
                      title="Preview course"
                      onClick={() => handleViewCourse(course.id)}
                    >
                      <PiEye />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AiTraining;
